import {
  useState,
  useMemo,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useRef,
  useTransition,
  startTransition,
  forwardRef,
  memo,
  CSSProperties,
  HTMLAttributes,
} from "react";
import {
  Text,
  Input,
  Button,
  Spinner,
  mergeClasses,
} from "@fluentui/react-components";
import {
  SearchRegular,
  DismissRegular,
  ArrowLeftRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import { OperationCard } from "./OperationCard";
import { CategoryCard } from "./CategoryCard";
import { ConnectorSection } from "./ConnectorSection";
import { AzureConnectorSection } from "./AzureConnectorSection";
import { SearchConnectorDetail, SearchConnectorGroupCard } from "./SearchConnectorViews";
import { AgentTypeMenu, type AgentType } from "./AgentTypeMenu";
import { applyAgentVariantDefaults, getAgentBrandColor } from "../helpers/agentVariant";
import { useOperationPickerPanelStyles } from "./OperationPickerPanel.styles";
import { PanelResizeHandle, usePanelWidth } from "@/components/shared/PanelResizeHandle";
import type { Connector } from "@/services/operationsService";
import type { OperationDefinition } from "@/types/operations";
import {
  getTriggerCategories,
  getActionCategories,
  type BrowseCategory,
  connectorToManifest,
  connectorSupportsOperationType,
} from "@/services/operationsService";
import {
  useOperationsGroupedByConnector,
  useAzureConnectorGroups,
} from "@/hooks/useOperations";
import { useSearchOperationsClient, MIN_SEARCH_LENGTH } from "@/hooks/useSearchOperationsClient";
import { type ConnectorSearchGroup } from "@/services/searchOperationsService";
import { useFavorites, useFavoriteOperations } from "@/hooks/useFavorites";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useAgentChatAguiStore } from "@/stores/agentChatAguiStore";
import { VariableSizeList, type ListChildComponentProps } from "react-window";
import {
  requestOperation,
  recurrenceOperation,
} from "@/data/clientOperations";
import codingAgentManifest from "@/data/manifests/codingAgent";
import { discoveryOperationToDefinition } from "@/services/operationsService";
import { matchesConnectorFilter } from "@/utils/connections";

const CODING_AGENT_ICON = codingAgentManifest.properties.iconUri;

type OperationPickerPanelProps = {
  /** Mode determines which operations are shown */
  mode: "trigger" | "action" | "all";
  /** Called when an operation is selected */
  onOperationSelect: (operation: OperationDefinition) => void;
  /** Called when the panel should close */
  onClose: () => void;
  /** Optional title override */
  title?: string;
};

/**
 * Panel for selecting operations to add to the workflow
 * Follows LogicAppsUX patterns with category browsing and search
 */
type OuterProps = HTMLAttributes<HTMLDivElement> & {
  style: CSSProperties;
};

type OperationPickerListItem =
  | { type: "header"; id: string; title: string }
  | {
      type: "builtInConnector";
      id: string;
      group: { connector: string; operations: OperationDefinition[] };
    }
  | { type: "azureConnector"; id: string; connector: Connector }
  | { type: "favoriteOperation"; id: string; operation: OperationDefinition };

type RowItemData = {
  items: OperationPickerListItem[];
  expandedConnectors: Record<string, boolean>;
  showAllOperations: Record<string, boolean>;
  onExpandChange: (connectorName: string, expanded: boolean) => void;
  onOpsLoaded: (connectorId: string, count: number) => void;
  onDisplayedCountChange: (connectorId: string, count: number, isSearching: boolean) => void;
  onShowAllToggle: (connectorId: string, showAll: boolean) => void;
  onOperationSelect: (operation: OperationDefinition) => void;
  actionType?: "triggers" | "actions" | undefined;
  sectionTitleClass: string;
  focusedConnectorIndex: number;
};

const MemoizedRow = memo(
  ({ index, style, data }: ListChildComponentProps<RowItemData>) => {
    const item = data.items[index];
    if (!item) return null;

    if (item.type === "header") {
      return (
        <div style={{ ...style, padding: "12px 4px" }}>
          <Text as="h3" className={data.sectionTitleClass}>
            {item.title}
          </Text>
        </div>
      );
    }

    if (item.type === "builtInConnector") {
      const isExpanded = data.expandedConnectors[item.id] ?? false;
      const showAll = data.showAllOperations[item.id] ?? false;
      return (
        <div style={{ ...style, paddingBottom: "4px" }}>
          <ConnectorSection
            connectorName={item.group.connector}
            operations={item.group.operations}
            onOperationSelect={data.onOperationSelect}
            isExpanded={isExpanded}
            onExpandedChange={(expanded) =>
              data.onExpandChange(item.id, expanded)
            }
            noMargin
            initialLimit={5}
            showAll={showAll}
            onShowAllChange={(showAll) =>
              data.onShowAllToggle(item.id, showAll)
            }
            onDisplayedCountChange={(count, isSearching) =>
              data.onDisplayedCountChange(item.id, count, isSearching)
            }
            headerTabIndex={index === data.focusedConnectorIndex ? 0 : -1}
            headerIndex={index}
          />
        </div>
      );
    }

    if (item.type === "favoriteOperation") {
      return (
        <div style={{ ...style, paddingBottom: "8px" }}>
          <OperationCard
            operation={item.operation}
            onClick={data.onOperationSelect}
          />
        </div>
      );
    }

    if (item.type === "azureConnector") {
      const isExpanded = data.expandedConnectors[item.id] ?? false;
      const showAll = data.showAllOperations[item.id] ?? false;
      return (
        <div style={{ ...style, paddingBottom: "4px" }}>
          <AzureConnectorSection
            connector={connectorToManifest(item.connector)}
            {...(data.actionType && { actionType: data.actionType })}
            onOperationSelect={data.onOperationSelect}
            isExpanded={isExpanded}
            onExpandedChange={(expanded) =>
              data.onExpandChange(item.id, expanded)
            }
            noMargin
            onOperationsLoaded={(count) => data.onOpsLoaded(item.id, count)}
            onDisplayedCountChange={(count, isSearching) =>
              data.onDisplayedCountChange(item.id, count, isSearching)
            }
            initialLimit={5}
            showAll={showAll}
            onShowAllChange={(showAll) =>
              data.onShowAllToggle(item.id, showAll)
            }
            headerTabIndex={index === data.focusedConnectorIndex ? 0 : -1}
            headerIndex={index}
          />
        </div>
      );
    }

    return null;
  }
);
MemoizedRow.displayName = "MemoizedRow";

interface SearchInputBarProps {
  className?: string;
  isSearching: boolean;
  onValueChange: (value: string) => void;
}

/**
 * Isolated, memoized search input that owns its own value state so a
 * keystroke only re-renders this subtree before paint. The parent is
 * notified via `onValueChange` and wraps that update in a transition.
 */
const SearchInputBar = memo(function SearchInputBar({
  className,
  isSearching,
  onValueChange,
}: SearchInputBarProps) {
  const [value, setValue] = useState("");
  const handleChange = useCallback(
    (_: unknown, data: { value: string }) => {
      setValue(data.value);
      onValueChange(data.value);
    },
    [onValueChange]
  );
  return (
    <Input
      className={className}
      placeholder="Search operations..."
      contentBefore={<SearchRegular />}
      contentAfter={
        isSearching ? (
          <Spinner size="extra-tiny" aria-label="Searching" role="status" />
        ) : null
      }
      value={value}
      onChange={handleChange}
      aria-label="Search operations"
      size="small"
      autoFocus
    />
  );
});
SearchInputBar.displayName = "SearchInputBar";

export const OperationPickerPanel = ({
  mode,
  onOperationSelect,
  onClose,
  title,
}: OperationPickerPanelProps) => {
  const styles = useOperationPickerPanelStyles();
  const isHostedAgentEnabled = useFeatureFlag('hostedAgents');
  const { width: panelWidth, setWidth: setPanelWidth, resetWidth: resetPanelWidth } = usePanelWidth({
    panelId: 'operation-picker',
    defaultWidth: 480,
    minWidth: 400,
    maxWidth: 720,
  });
  const queuePrompt = useAgentChatAguiStore((s) => s.queuePrompt);
  const isStreaming = useAgentChatAguiStore((s) => s.isStreaming);
  const pendingPrompt = useAgentChatAguiStore((s) => s.pendingPrompt);
  const isAiBusy = isStreaming || pendingPrompt !== null;

  // Custom outer element for VariableSizeList with custom scrollbar
  // MUST be memoized to prevent unmounting/remounting on every render
  const CustomScrollbarOuter = useMemo(
    () =>
      forwardRef<HTMLDivElement, OuterProps>(
        ({ style, className, ...rest }, ref) => (
          <div
            ref={ref}
            style={{
              ...style, // REQUIRED
              overflow: "auto", // optional, but explicit
            }}
            className={mergeClasses(styles.virtualizedList, className)}
            {...rest}
          />
        )
      ),
    [styles.virtualizedList]
  );

  // Map of immediate operation IDs to their operation objects
  const immediateOperations = useMemo(
    () => ({
      manual: discoveryOperationToDefinition(requestOperation),
      recurrence: discoveryOperationToDefinition(recurrenceOperation),
    }),
    []
  );
  const [searchQuery, setSearchQuery] = useState("");
  // Wrap parent search-state updates in a transition so the heavy
  // downstream re-render never blocks input paint, and defer the value
  // used by the filtering pipeline so a keystroke yields to the next
  // frame even when readers consume `searchQuery` synchronously.
  const [, startSearchTransition] = useTransition();
  const deferredSearchQuery = useDeferredValue(searchQuery);
  // True while React is still committing the deferred render; drives the
  // inline spinner in the search input.
  const isSearchTyping = searchQuery !== deferredSearchQuery;
  // Drives view-mode switching (categories vs. search vs. connector list).
  // Uses the lagged value during typing to avoid flashing empty/loading
  // states mid-keystroke, but flips to "" immediately when the user clears
  // (back button, select-all-delete) so stale Fuse results don't linger
  // for a frame while `useDeferredValue` catches up.
  const effectiveSearchQuery = searchQuery === "" ? "" : deferredSearchQuery;
  // Bumped to remount <SearchInputBar/> when the parent needs to reset its
  // value externally — the input owns its state for typing perf, so props
  // alone can't clear it.
  const [searchInputResetKey, setSearchInputResetKey] = useState(0);
  const [selectedCategory, setSelectedCategory] =
    useState<BrowseCategory | null>(null);
  const { favorites: favoriteConnectorIds } = useFavorites();
  const { favoriteOperations } = useFavoriteOperations();

  // When the user selects the dynamic "Favorites" category, resolve its
  // connector ID filter from the favorites store. Use a sentinel ID when the
  // user has no favorites yet so the existing filter pipeline yields an empty
  // list (the UI then shows a dedicated empty state).
  const resolvedSelectedCategory = useMemo<BrowseCategory | null>(() => {
    if (!selectedCategory) return null;
    if (selectedCategory.type !== 'favorites') return selectedCategory;
    return {
      ...selectedCategory,
      connectorFilters: {
        connectorIds:
          favoriteConnectorIds.length > 0
            ? favoriteConnectorIds
            : ['__otto_no_favorites__'],
      },
    };
  }, [selectedCategory, favoriteConnectorIds]);

  const contentRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<VariableSizeList>(null);
  const listOuterRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(600);

  // Track which connectors are expanded
  const [expandedConnectors, setExpandedConnectors] = useState<
    Record<string, boolean>
  >({});

  // Track operation counts for Azure connectors (for dynamic height calculation)
  const [azureOperationCounts, setAzureOperationCounts] = useState<
    Record<string, number>
  >({});

  // Track displayed counts (after the per-section internal search filter) for
  // both Azure and built-in connectors. Shared since both share the same
  // {count, isSearching} shape and feed the same row-height calc.
  const [displayedCounts, setDisplayedCounts] = useState<
    Record<string, { count: number; isSearching: boolean }>
  >({});

  // Track which connectors are showing all operations (vs limited view)
  const [showAllOperations, setShowAllOperations] = useState<
    Record<string, boolean>
  >({});

  // Track which connector is focused for roving tabindex keyboard navigation
  const [focusedConnectorIndex, setFocusedConnectorIndex] = useState(0);


  // Agent type submenu state (shown when clicking Agent operation)
  const [agentMenuState, setAgentMenuState] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    operation: OperationDefinition | null;
  }>({ open: false, anchorEl: null, operation: null });

  // Track which connector the user has drilled into from the search results
  // (matches the Logic Apps Designer "See more" → connector detail flow).
  const [drillInConnector, setDrillInConnector] = useState<ConnectorSearchGroup | null>(null);

  // Determine category based on mode
  const searchCategory =
    mode === "trigger" ? "trigger" : mode === "action" ? "action" : "all";

  // Client-side search over the prefetched corpus (see usePrefetchOperations
  // mounted in AppLayout). The hook applies its own 250ms debounce inside,
  // so `clientSearchGroups` stays on the previous term until the debounced
  // Fuse run lands — we read `isPending` to distinguish "actively
  // searching" from "genuinely no results" so we don't flash the empty
  // state mid-keystroke.
  const {
    groups: clientSearchGroups,
    isLoadingMore: searchIsLoadingMore,
    indexSize: searchIndexSize,
    isPending: searchIsPending,
  } = useSearchOperationsClient(deferredSearchQuery, searchCategory);

  // True any time the displayed query and the index aren't in sync —
  // either because React hasn't committed the deferred render yet, or
  // because the hook's internal debounce hasn't fired. Drives both the
  // input spinner and the "don't show empty state yet" gate below.
  const isSearchSettling = isSearchTyping || searchIsPending;

  // Trimmed length of the committed query. Used by the render switch
  // below to distinguish "no query", "too-short query" (hint), and
  // "real query" (results or empty state).
  const trimmedSearchLength = deferredSearchQuery.trim().length;

  // Block-loading state: only show the big "Loading operations…" spinner
  // when we genuinely have nothing to search yet. If any operations are in
  // the index, results are usable immediately; the subtle inline hint below
  // the search input communicates that more are still streaming in.
  const isLoading = !!effectiveSearchQuery && searchIsLoadingMore && searchIndexSize === 0;

  // Get built-in operations grouped by connector
  const { data: builtInGroupsRaw, isLoading: builtInLoading } =
    useOperationsGroupedByConnector(searchCategory);

  // Get Azure connector groups
  const { data: azureConnectorsRaw, isLoading: azureConnectorsLoading } =
    useAzureConnectorGroups();

  // Filter built-in groups and Azure connectors based on selected category
  const expandAgentVariants = useCallback(
    <T extends { type?: string; defaultInputs?: unknown }>(op: T): T[] => {
      if (op.type !== 'Agent') return [op];
      const baseInputs = (op.defaultInputs ?? {}) as Record<string, unknown>;
      const nativeVariant = {
        ...op,
        id: 'agent-native',
        name: 'Workflow Agent',
        description: 'Use built-in connectors and tools',
        brandColor: getAgentBrandColor('native'),
      } as T;
      const codingVariant = {
        ...op,
        id: 'agent-coding',
        name: 'Coding agent',
        description: 'Runs in a sandboxed harness with repos and file outputs',
        brandColor: getAgentBrandColor('coding'),
        icon: CODING_AGENT_ICON,
        defaultInputs: applyAgentVariantDefaults(baseInputs, 'coding'),
      } as T;
      const foundryVariant = {
        ...op,
        id: 'agent-foundry',
        name: 'Foundry Agent',
        description: 'Configure an agent from Azure AI Foundry',
        brandColor: getAgentBrandColor('foundry'),
        defaultInputs: applyAgentVariantDefaults(baseInputs, 'foundry'),
      } as T;
      const variants = [nativeVariant, codingVariant, foundryVariant];
      // Hosted agent is gated on its own flag.
      if (isHostedAgentEnabled) {
        variants.push({
          ...op,
          id: 'agent-hosted',
          name: 'Foundry Hosted agent',
          description: 'Pre-provisioned container agent with input files',
          brandColor: getAgentBrandColor('hosted'),
          defaultInputs: applyAgentVariantDefaults(baseInputs, 'hosted'),
        } as T);
      }
      return variants;
    },
    [isHostedAgentEnabled]
  );

  // Variant expansion for raw discovery-shape ops (search results). The
  // displayed label comes from `properties.summary`, so variants must set
  // `properties.summary` / `properties.description` / `properties.brandColor`
  // — overriding the top-level `name` alone is not enough.
  const expandAgentDiscoveryVariants = useCallback(
    (op: ConnectorSearchGroup['matches'][number]['operation']) => {
      if (op.type !== 'Agent') return [op];
      const baseInputs = (op.defaultInputs ?? {}) as Record<string, unknown>;
      const makeVariant = (
        id: string,
        summary: string,
        description: string,
        brandColor: string,
        defaultInputs: unknown,
        iconUri?: string,
      ) => ({
        ...op,
        id,
        name: id,
        defaultInputs,
        properties: {
          ...op.properties,
          summary,
          description,
          brandColor,
          ...(iconUri ? { iconUri } : {}),
        },
      });
      const nativeVariant = makeVariant(
        'agent-native',
        'Workflow agent',
        'Use built-in connectors and tools',
        getAgentBrandColor('native'),
        baseInputs,
      );
      const codingVariant = makeVariant(
        'agent-coding',
        'Coding agent',
        'Runs in a sandboxed harness with repos and file outputs',
        getAgentBrandColor('coding'),
        applyAgentVariantDefaults(baseInputs, 'coding'),
        CODING_AGENT_ICON,
      );
      const foundryVariant = makeVariant(
        'agent-foundry',
        'Foundry agent',
        'Configure an agent from Azure AI Foundry',
        getAgentBrandColor('foundry'),
        applyAgentVariantDefaults(baseInputs, 'foundry'),
      );
      const variants = [nativeVariant, codingVariant, foundryVariant];
      // Hosted agent is gated on its own flag.
      if (isHostedAgentEnabled) {
        variants.push(makeVariant(
          'agent-hosted',
          'Foundry Hosted agent',
          'Pre-provisioned container agent with input files',
          getAgentBrandColor('hosted'),
          applyAgentVariantDefaults(baseInputs, 'hosted'),
        ));
      }
      return variants;
    },
    [isHostedAgentEnabled]
  );

  const builtInGroups = useMemo(() => {
    const filtered = filterByConnectorIds(builtInGroupsRaw, resolvedSelectedCategory, (group) =>
      group.operations.map((op) => op.connectorId || "")
    );
    // Expand single Agent operation into Foundry / Native / Coding variants
    return filtered?.map((group) => {
      const hasAgent = group.operations.some((op) => op.type === 'Agent');
      if (!hasAgent) return group;
      const expanded = group.operations.flatMap((op) => expandAgentVariants(op));
      return { ...group, operations: expanded };
    });
  }, [builtInGroupsRaw, resolvedSelectedCategory, expandAgentVariants]);

  const azureConnectors = useMemo(() => {
    let filtered = filterByConnectorIds(
      azureConnectorsRaw,
      resolvedSelectedCategory,
      (connector) => [connector.id]
    );

    // Apply the operation-type filter (curated wrong-type exclusions,
    // override list, then declared capabilities). See
    // `connectorSupportsOperationType` for the precedence rules.
    if (mode === "trigger" || mode === "action") {
      const type = mode === "trigger" ? "triggers" : "actions";
      filtered = filtered?.filter((connector) =>
        connectorSupportsOperationType(connector, type),
      );
    }

    return filtered;
  }, [azureConnectorsRaw, resolvedSelectedCategory, mode]);

  // Determine action type for Azure connectors
  const actionType =
    mode === "trigger"
      ? ("triggers" as const)
      : mode === "action"
      ? ("actions" as const)
      : undefined;

  // Create combined list of all items to render (headers + connectors).
  // When in the Favorites category, prepend a "Favorite operations" section
  // so favorited operations and favorited connectors share the same
  // virtualized list (no separate height accounting needed).
  const combinedListItems = useMemo<OperationPickerListItem[]>(() => {
    const items: OperationPickerListItem[] = [];

    const isFavoritesView = selectedCategory?.type === "favorites";
    if (isFavoritesView && favoriteOperations.length > 0) {
      items.push({
        type: "header",
        title: `Favorite Operations (${favoriteOperations.length})`,
        id: "header-favorite-operations",
      });
      favoriteOperations.forEach((operation) => {
        items.push({
          type: "favoriteOperation",
          operation,
          id: `favorite-op-${operation.id}`,
        });
      });
    }

    // Add built-in operations section
    if (builtInGroups && builtInGroups.length > 0) {
      items.push({
        type: "header",
        title: isFavoritesView
          ? "Favorite Connectors \u2014 Built-in"
          : "Built-in Operations",
        id: "header-builtin",
      });
      builtInGroups.forEach((group) => {
        items.push({
          type: "builtInConnector",
          group,
          id: `builtin-${group.connector}`,
        });
      });
    }

    // Add azure connectors section
    if (azureConnectors && azureConnectors.length > 0) {
      items.push({
        type: "header",
        title: isFavoritesView
          ? `Favorite Connectors \u2014 Azure (${azureConnectors.length})`
          : `Azure Connectors (${azureConnectors.length})`,
        id: "header-azure",
      });
      azureConnectors.forEach((connector) => {
        items.push({
          type: "azureConnector",
          connector,
          id: `azure-${connector.id}`,
        });
      });
    }

    return items;
  }, [builtInGroups, azureConnectors, selectedCategory, favoriteOperations]);

  // Indices of connector rows (excluding headers) for keyboard navigation
  const connectorIndices = useMemo(
    () => combinedListItems
      .map((item, i) => item.type !== 'header' ? i : -1)
      .filter(i => i >= 0),
    [combinedListItems]
  );

  // Reset focused connector when the list changes
  useEffect(() => {
    if (connectorIndices.length > 0) {
      setFocusedConnectorIndex(connectorIndices[0]!);
    }
  }, [connectorIndices]);

  // Keyboard handler for arrow-key navigation across virtualized connector rows
  const handleListKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentPos = connectorIndices.indexOf(focusedConnectorIndex);
    if (currentPos === -1) return;

    let nextPos = currentPos;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        nextPos = Math.min(currentPos + 1, connectorIndices.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        nextPos = Math.max(currentPos - 1, 0);
        break;
      case 'Home':
        e.preventDefault();
        nextPos = 0;
        break;
      case 'End':
        e.preventDefault();
        nextPos = connectorIndices.length - 1;
        break;
      default:
        return;
    }

    const nextIndex = connectorIndices[nextPos]!;
    setFocusedConnectorIndex(nextIndex);
    listRef.current?.scrollToItem(nextIndex, 'smart');
    // After scroll + render, find and focus the target header by its index attribute.
    // We use data-connector-index instead of position in querySelectorAll because
    // virtualized lists only mount a subset of items.
    requestAnimationFrame(() => {
      const target = listOuterRef.current?.querySelector<HTMLButtonElement>(
        `[data-connector-header][data-connector-index="${nextIndex}"]`
      );
      target?.focus();
    });
  }, [focusedConnectorIndex, connectorIndices]);

  // Get categories based on mode
  const categories = useMemo(() => {
    if (mode === "trigger") {
      return getTriggerCategories();
    }
    return getActionCategories();
  }, [mode]);

  // Set of built-in connector groups keyed by connectorId. Used by the
  // search drill-in view to show ALL operations for a built-in connector
  // when the user clicks "View all operations".
  const builtInConnectorsById = useMemo(() => {
    const map: Record<string, OperationDefinition[]> = {};
    if (builtInGroups) {
      for (const group of builtInGroups) {
        const connectorId = group.operations?.[0]?.connectorId?.toLowerCase();
        if (connectorId) {
          map[connectorId] = group.operations;
        }
      }
    }
    return map;
  }, [builtInGroups]);

  // Map Azure connector id (lowercased) to its manifest object. Used by the
  // search drill-in view as a hydrated source for the connector when available
  // — falls back to a synthetic manifest derived from the matched operation
  // when the legacy Azure groups hook hasn't loaded that connector yet, so
  // search drill-in never silently drops a connector.
  const azureConnectorsById = useMemo(() => {
    const map: Record<string, Connector> = {};
    if (azureConnectors) {
      for (const connector of azureConnectors) {
        map[connector.id.toLowerCase()] = connector;
      }
    }
    return map;
  }, [azureConnectors]);

  // Apply the selected category's connector filter to the search groups so
  // categories like "Favorites" still narrow the search results. Drops
  // matches whose connector isn't in the filter, and drops groups that end
  // up with no surviving matches. Search groups come directly from the
  // search service — no intersection with legacy connector maps, so
  // connectors are never silently dropped because a lazy hook hasn't loaded
  // them yet. Agent matches are expanded in-place into Foundry / Native /
  // Coding variants so all three are discoverable in search, mirroring the
  // browse path.
  const filteredSearchGroups = useMemo<ConnectorSearchGroup[]>(() => {
    if (!deferredSearchQuery) return [];
    const connectorIds = resolvedSelectedCategory?.connectorFilters?.connectorIds;
    const expandMatches = (matches: ConnectorSearchGroup['matches']): ConnectorSearchGroup['matches'] =>
      matches.flatMap((m) => {
        if (m.operation.type !== 'Agent') return [m];
        return expandAgentDiscoveryVariants(m.operation).map((variantOp) => ({
          ...m,
          operation: variantOp,
        }));
      });

    if (!connectorIds || connectorIds.length === 0) {
      return clientSearchGroups.map((group) => ({
        ...group,
        matches: expandMatches(group.matches),
      }));
    }

    const result: ConnectorSearchGroup[] = [];
    for (const group of clientSearchGroups) {
      const survivingMatches = group.matches.filter((m) => {
        const opConnectorId = m.operation.properties?.api?.id ?? '';
        return connectorIds.some((id) => matchesConnectorFilter(opConnectorId, id));
      });
      if (survivingMatches.length > 0) {
        result.push({ ...group, matches: expandMatches(survivingMatches) });
      }
    }
    return result;
  }, [clientSearchGroups, deferredSearchQuery, resolvedSelectedCategory, expandAgentDiscoveryVariants]);

  // Reset drill-in whenever the (deferred) search query changes —
  // otherwise we'd be showing a connector detail view that no longer
  // matches the new query. Keyed on the deferred value so this doesn't
  // fire mid-keystroke before the heavy render has caught up.
  useEffect(() => {
    setDrillInConnector(null);
  }, [deferredSearchQuery]);

  const handleSearchChange = useCallback(
    (value: string) => {
      // Run the parent state update inside a transition. Combined with
      // the local state owned by <SearchInputBar/>, this guarantees that
      // the input's paint is never blocked by this panel's reconciliation
      // — even at "hold a key down" / Ctrl+A+Delete speed.
      startSearchTransition(() => {
        setSearchQuery(value);
      });
    },
    []
  );

  const handleOperationClick = useCallback(
    (operation: OperationDefinition, _event?: React.MouseEvent) => {
      onOperationSelect(operation);
    },
    [onOperationSelect]
  );

  // Tracks the search-list scroll position so we can return the user
  // exactly where they clicked "View all" after they press "Back to
  // results". The snapshot MUST happen synchronously in the click
  // handler (before `setState`) — once React applies `display: none`
  // to the list the parent scroll container's content height shrinks
  // and `scrollTop` clamps to 0, so an effect-based snapshot would
  // always capture 0.
  const savedSearchScrollRef = useRef(0);
  // Ref to the hidden-while-drilled-in search list wrapper. We toggle
  // the `inert` attribute on it imperatively (instead of `aria-hidden`)
  // because `aria-hidden` on an ancestor of a focused element is an
  // accessibility violation — Chromium logs a warning and the focused
  // button is still reachable. `inert` blocks focus, pointer events,
  // and AT exposure in one go, and the browser blurs any focused
  // descendant automatically.
  const searchListRef = useRef<HTMLDivElement>(null);

  // Drill-in toggles wrap the state update in `startTransition` so the
  // click feedback (and the "Back to results" button press in particular)
  // never blocks on the heavy re-render of dozens of
  // `SearchConnectorGroupCard`s. Stable references also let the memoized
  // card component skip re-rendering when an unrelated parent state changes.
  const handleSearchDrillIn = useCallback((group: ConnectorSearchGroup) => {
    savedSearchScrollRef.current = contentRef.current?.scrollTop ?? 0;
    startTransition(() => {
      setDrillInConnector(group);
    });
  }, []);

  const handleSearchDrillOut = useCallback(() => {
    startTransition(() => {
      setDrillInConnector(null);
    });
  }, []);

  // Restore the saved scroll position on the way back. `useLayoutEffect`
  // runs after the list is visible again but before paint, so the user
  // never sees a flash at the top before the jump.
  useLayoutEffect(() => {
    // Toggle `inert` on the list when entering / leaving drill-in.
    if (searchListRef.current) {
      if (drillInConnector) {
        searchListRef.current.setAttribute("inert", "");
      } else {
        searchListRef.current.removeAttribute("inert");
      }
    }
    if (drillInConnector) {
      // Entering drill-in: start the detail view at the top.
      if (contentRef.current) contentRef.current.scrollTop = 0;
    } else if (savedSearchScrollRef.current > 0 && contentRef.current) {
      contentRef.current.scrollTop = savedSearchScrollRef.current;
    }
  }, [drillInConnector]);

  const handleAgentTypeSelect = useCallback(
    (agentType: AgentType) => {
      const operation = agentMenuState.operation;
      if (!operation) return;

      // Agent operations always have object inputs, so cast is safe.
      const baseInputs = (operation.defaultInputs ?? {}) as Record<string, unknown>;
      const defaultInputs = applyAgentVariantDefaults(baseInputs, agentType);
      onOperationSelect(
        defaultInputs === baseInputs ? operation : { ...operation, defaultInputs },
      );
      setAgentMenuState({ open: false, anchorEl: null, operation: null });
    },
    [agentMenuState.operation, onOperationSelect]
  );

  const handleAgentMenuClose = useCallback(() => {
    setAgentMenuState({ open: false, anchorEl: null, operation: null });
  }, []);

  const handleCategoryClick = useCallback(
    (categoryKey: string) => {
      const category = categories.find((c) => c.key === categoryKey);
      if (!category) return;

      // For immediate categories, select the operation directly
      if (category.type === "immediate" && category.operationId) {
        const operation = immediateOperations[category.operationId as keyof typeof immediateOperations];
        if (operation) {
          onOperationSelect(operation);
          return;
        }
      }

      // For browse categories, show filtered view
      setSelectedCategory(category);
    },
    [categories, immediateOperations, onOperationSelect]
  );

  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null);
    setExpandedConnectors({});
    // Urgent (not via startSearchTransition) so `effectiveSearchQuery`
    // flips to "" in the same commit as `selectedCategory`.
    setSearchQuery("");
    setSearchInputResetKey((k) => k + 1);
  }, []);

  // Reset VariableSizeList caches when the visible item set changes so row
  // heights are recomputed for the new category. Keyed on the deferred
  // search value so the reset fires in the same low-priority commit as
  // the rest of the panel — not on every keystroke.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, false);
    }
  }, [deferredSearchQuery, selectedCategory, combinedListItems]);

  // Reset the cached height for a single virtualized row by id. Each
  // per-row callback (expand, lazy load, search, see-all) needs this so
  // VariableSizeList re-measures after its own state changed.
  const resetRowHeightForId = useCallback(
    (id: string) => {
      const index = combinedListItems.findIndex((item) => item.id === id);
      if (index !== -1 && listRef.current) {
        listRef.current.resetAfterIndex(index, false);
      }
    },
    [combinedListItems]
  );

  const handleConnectorExpandChange = useCallback(
    (connectorName: string, expanded: boolean) => {
      setExpandedConnectors((prev) => ({
        ...prev,
        [connectorName]: expanded,
      }));
      resetRowHeightForId(connectorName);
    },
    [resetRowHeightForId]
  );

  const handleAzureOperationsLoaded = useCallback(
    (connectorId: string, count: number) => {
      setAzureOperationCounts((prev) => {
        if (prev[connectorId] === count) return prev;
        return { ...prev, [connectorId]: count };
      });
      resetRowHeightForId(connectorId);
    },
    [resetRowHeightForId]
  );

  // Single handler for both Azure + built-in displayed counts (same shape).
  const handleDisplayedCountChange = useCallback(
    (connectorId: string, count: number, isSearching: boolean) => {
      setDisplayedCounts((prev) => {
        const existing = prev[connectorId];
        if (existing?.count === count && existing?.isSearching === isSearching) {
          return prev;
        }
        return { ...prev, [connectorId]: { count, isSearching } };
      });
      resetRowHeightForId(connectorId);
    },
    [resetRowHeightForId]
  );

  const handleShowAllToggle = useCallback(
    (connectorId: string, showAll: boolean) => {
      setShowAllOperations((prev) => ({
        ...prev,
        [connectorId]: showAll,
      }));
      resetRowHeightForId(connectorId);
    },
    [resetRowHeightForId]
  );

  // Determine panel title
  const panelTitle =
    selectedCategory?.title ||
    title ||
    (mode === "trigger" ? "Add a trigger" : "Add an action");

  const showBackButton = useMemo(() => selectedCategory !== null, [selectedCategory]);
  const showCategories = useMemo(() => !effectiveSearchQuery && !selectedCategory, [effectiveSearchQuery, selectedCategory]);
  const showConnectorList = useMemo(() => !effectiveSearchQuery && selectedCategory, [effectiveSearchQuery, selectedCategory]);

  // Calculate row height based on item type and expansion state
  const getItemSize = useCallback(
    (index: number) => {
      const item = combinedListItems[index];
      if (!item) return 40;

      if (item.type === "header") return 40;
      // OperationCard renders ~52px (40px min + two-line content) + 8px gap
      // to match connector-section row spacing.
      if (item.type === "favoriteOperation") return 60;

      const isExpanded = expandedConnectors[item.id] ?? false;
      const rowGap = 4;
      if (!isExpanded) return 68 + rowGap;

      const totalOperationCount =
        item.type === "builtInConnector"
          ? item.group.operations.length
          : azureOperationCounts[item.id];

      // Azure connector still loading — use a coarse estimate.
      if (totalOperationCount === undefined) return 150 + rowGap;

      return computeConnectorSectionHeight({
        totalOperationCount,
        showAll: showAllOperations[item.id] ?? false,
        displayedData: displayedCounts[item.id],
        rowGap,
      });
    },
    [
      combinedListItems,
      expandedConnectors,
      azureOperationCounts,
      displayedCounts,
      showAllOperations,
    ]
  );

  const itemData = useMemo(
    () => ({
      items: combinedListItems,
      expandedConnectors,
      showAllOperations,
      onExpandChange: handleConnectorExpandChange,
      onOpsLoaded: handleAzureOperationsLoaded,
      onDisplayedCountChange: handleDisplayedCountChange,
      onShowAllToggle: handleShowAllToggle,
      onOperationSelect: handleOperationClick,
      actionType,
      sectionTitleClass: styles.sectionTitle,
      focusedConnectorIndex,
    }),
    [
      combinedListItems,
      expandedConnectors,
      showAllOperations,
      handleConnectorExpandChange,
      handleAzureOperationsLoaded,
      handleDisplayedCountChange,
      handleShowAllToggle,
      handleOperationClick,
      actionType,
      styles.sectionTitle,
      focusedConnectorIndex,
    ]
  );

  // Measure the available height for the virtualized list
  useEffect(() => {
    if (!contentRef.current || !showConnectorList) {
      return;
    }

    const updateHeight = () => {
      if (contentRef.current) {
        const contentRect = contentRef.current.getBoundingClientRect();
        // Use full content height for the virtualized list
        setListHeight(Math.max(400, contentRect.height));
      }
    };

    // Delay to ensure DOM is rendered
    const timer = setTimeout(updateHeight, 100);

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(contentRef.current);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [showConnectorList]);

  return (
    <div className={styles.panel} role="region" aria-label={panelTitle} style={{ width: panelWidth }}>
      <PanelResizeHandle
        edge="left"
        value={panelWidth}
        min={400}
        max={720}
        defaultValue={480}
        ariaLabel="Resize Operation Picker"
        collapsible={false}
        onResize={setPanelWidth}
        onReset={resetPanelWidth}
      />
      <div className={styles.header}>
        {showBackButton && (
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            size="small"
            onClick={handleBackToCategories}
            aria-label="Back to categories"
          />
        )}
        <Text as="h2" className={styles.headerTitle}>{panelTitle}</Text>
        <Button
          icon={<DismissRegular />}
          appearance="subtle"
          size="small"
          onClick={onClose}
          aria-label="Close panel"
        />
      </div>

      <div className={styles.searchContainer} role="search">
        <SearchInputBar
          key={searchInputResetKey}
          className={styles.searchInput}
          isSearching={isSearchSettling}
          onValueChange={handleSearchChange}
        />
        {searchIsLoadingMore && searchIndexSize > 0 && (
          <div
            className={styles.loadingMoreHint}
            role="status"
            aria-live="polite"
          >
            <Spinner size="extra-tiny" />
            <Text size={200}>Loading more connectors…</Text>
          </div>
        )}
      </div>

      <div
        className={
          showConnectorList && combinedListItems.length > 0
            ? styles.contentVirtualized
            : styles.content
        }
        ref={contentRef}
        aria-live="polite"
        aria-atomic="true"
      >
        {showConnectorList ? (
          // Show combined virtualized connector list (Built-in + Azure)
          builtInLoading || azureConnectorsLoading ? (
            <div className={styles.loadingState}>
              <Spinner size="medium" />
              <Text>Loading connectors...</Text>
            </div>
          ) : combinedListItems.length > 0 ? (
            <div onKeyDown={handleListKeyDown} role="list" aria-label="Connector sections">
            <VariableSizeList
              ref={listRef}
              outerRef={listOuterRef}
              height={listHeight}
              itemCount={combinedListItems.length}
              itemSize={getItemSize}
              width="100%"
              overscanCount={5}
              outerElementType={CustomScrollbarOuter}
              itemData={itemData}
            >
              {MemoizedRow}
            </VariableSizeList>
          </div>
          ) : selectedCategory?.type === 'favorites' ? (
            <div className={styles.emptyState}>
              <Text weight="semibold">No favorites yet</Text>
              <Text>
                Tap the star on any operation or connector to add it here for
                quick access.
              </Text>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Text>No connectors found</Text>
            </div>
          )
        ) : isLoading ? (
          <div className={styles.loadingState}>
            <Spinner size="medium" />
            <Text>Loading operations...</Text>
          </div>
        ) : showCategories ? (
          // Show category cards when no search and no category selected
          <div className={styles.categoriesList}>
            {categories.map((category) =>
              category.visible === false ? null : (
                <CategoryCard
                  key={category.key}
                  category={category}
                  onClick={handleCategoryClick}
                />
              )
            )}
          </div>
        ) : trimmedSearchLength > 0 && trimmedSearchLength < MIN_SEARCH_LENGTH ? (
          // The search hook gates Fuse at MIN_SEARCH_LENGTH chars because
          // 1-char queries against a 30k-op corpus produce a flood of
          // low-relevance hits and block the main thread for ~100ms+.
          // Surface the gate explicitly here instead of falling through
          // to the "No operations found" empty state, which misled users
          // into thinking the picker was broken for short queries.
          <div className={styles.loadingState}>
            <Text>Type at least {MIN_SEARCH_LENGTH} characters to search…</Text>
          </div>
        ) : effectiveSearchQuery && filteredSearchGroups.length === 0 && !searchIsLoadingMore && !isSearchSettling ? (
          <div className={styles.emptyState}>
            <Text>No operations found for &ldquo;{deferredSearchQuery}&rdquo;</Text>
            <button
              className={styles.askOttoCard}
              onClick={() => {
                const noun = mode === 'trigger' ? 'trigger' : mode === 'action' ? 'action' : 'operation';
                queuePrompt(
                  `Help me add a ${noun} for: ${deferredSearchQuery}`,
                  { type: 'workflow' },
                  'picker-no-results',
                );
              }}
              disabled={isAiBusy}
              type="button"
              aria-label={`Ask Copilot to help with "${deferredSearchQuery}"`}
            >
              <SparkleRegular className={styles.askOttoIcon} />
              <Text className={styles.askOttoLabel}>Ask Copilot</Text>
              <Text className={styles.askOttoHint}>
                {isAiBusy ? 'Copilot is busy…' : 'Find or create this action with AI'}
              </Text>
            </button>
          </div>
        ) : effectiveSearchQuery && filteredSearchGroups.length === 0 ? (
          // No matches yet, but either the debounced Fuse run hasn't
          // fired or Azure pages are still streaming in. Use a compact
          // inline hint instead of a full-takeover spinner so the UI
          // doesn't appear to freeze between keystrokes.
          <div className={styles.loadingState}>
            <Spinner size="medium" />
            <Text>
              {isSearchSettling
                ? 'Searching\u2026'
                : 'Still loading more connectors\u2026 results will update.'}
            </Text>
          </div>
        ) : effectiveSearchQuery ? (
          // Search results: connector-grouped, like the Logic Apps Designer.
          // Each connector shows its top matches plus a "View all operations
          // from {connector}" button that drills into the full connector view.
          //
          // The list and the drill-in detail are rendered as siblings (instead
          // of swapped via ternary) so the search cards stay mounted while
          // the user is looking at a single connector's operations. That way
          // "Back to results" is an instant `display: none` toggle rather
          // than a remount of dozens of Accordion cards.
          <>
            <div
              ref={searchListRef}
              className={mergeClasses(
                styles.searchGroupList,
                drillInConnector ? styles.searchGroupListHidden : undefined,
              )}
            >
              {filteredSearchGroups.map((group) => (
                <SearchConnectorGroupCard
                  key={group.connectorId}
                  group={group}
                  searchQuery={deferredSearchQuery}
                  onOperationSelect={handleOperationClick}
                  onViewAll={handleSearchDrillIn}
                  styles={styles}
                />
              ))}
              {searchIsLoadingMore && (
                <div className={styles.loadingMoreHint} aria-live="polite">
                  <Spinner size="extra-tiny" />
                  <span>Loading more connectors&hellip;</span>
                </div>
              )}
            </div>
            {drillInConnector && (
              <SearchConnectorDetail
                group={drillInConnector}
                {...(azureConnectorsById[drillInConnector.connectorId.toLowerCase()] && {
                  hydratedAzureConnector:
                    azureConnectorsById[drillInConnector.connectorId.toLowerCase()],
                })}
                {...(builtInConnectorsById[drillInConnector.connectorId.toLowerCase()] && {
                  builtInOperations:
                    builtInConnectorsById[drillInConnector.connectorId.toLowerCase()],
                })}
                {...(mode === "trigger"
                  ? { actionType: "triggers" as const }
                  : mode === "action"
                  ? { actionType: "actions" as const }
                  : {})}
                onOperationSelect={handleOperationClick}
                onBack={handleSearchDrillOut}
                styles={styles}
              />
            )}
          </>
        ) : null}
      </div>
      <AgentTypeMenu
        open={agentMenuState.open}
        anchorEl={agentMenuState.anchorEl}
        onSelect={handleAgentTypeSelect}
        onClose={handleAgentMenuClose}
      />
    </div>
  );
};

function filterByConnectorIds<T>(
  items: T[] | undefined,
  selectedCategory: BrowseCategory | null,
  getCandidateIds: (item: T) => string[]
) {
  if (!items || !selectedCategory?.connectorFilters) return items;

  const { connectorIds } = selectedCategory.connectorFilters;

  if (!connectorIds || connectorIds.length === 0) return items;

  return items.filter((item) =>
    getCandidateIds(item).some((candidate) =>
      connectorIds.some((id) => matchesConnectorFilter(candidate, id))
    )
  );
}

/**
 * Pixel height of an expanded connector section for the virtualized list.
 * Derived from the visual budget:
 *   header (68px, padding + icon + connectorInfo + container border)
 * + per-section search box (50px) when total ops > initialLimit
 * + list vertical padding (24px) + operation rows (40px each)
 * + 8px between rows
 * + 32px "see all" button when truncated
 * + caller-provided row gap between sections.
 */
function computeConnectorSectionHeight(args: {
  totalOperationCount: number;
  showAll: boolean;
  displayedData: { count: number; isSearching: boolean } | undefined;
  rowGap: number;
  initialLimit?: number;
}): number {
  const { totalOperationCount, showAll, displayedData, rowGap } = args;
  const initialLimit = args.initialLimit ?? 5;
  const headerHeight = 68;
  const listPadding = 24;
  const operationHeight = 40;
  const seeAllButtonHeight = 32;
  const searchBoxHeight = totalOperationCount > initialLimit ? 50 : 0;

  const isSearching = displayedData?.isSearching ?? false;
  const filteredCount = isSearching
    ? displayedData?.count ?? totalOperationCount
    : totalOperationCount;

  if (isSearching && filteredCount === 0) {
    const emptyStateHeight = 48;
    return headerHeight + searchBoxHeight + emptyStateHeight + rowGap;
  }

  if (showAll || filteredCount <= initialLimit) {
    const gapHeight = filteredCount > 0 ? (filteredCount - 1) * 8 : 0;
    return (
      headerHeight +
      searchBoxHeight +
      listPadding +
      filteredCount * operationHeight +
      gapHeight +
      rowGap
    );
  }

  const gapHeight = initialLimit > 0 ? (initialLimit - 1) * 8 : 0;
  return (
    headerHeight +
    searchBoxHeight +
    listPadding +
    initialLimit * operationHeight +
    gapHeight +
    seeAllButtonHeight +
    rowGap
  );
}

export default OperationPickerPanel;
